import { FieldTypes } from '../../../constants/FieldTypes'
import { CheckboxField, DropdownField, FieldMap, TextAreaField, TextField } from '../../../types'
import {
  AudioOnlyReason,
  InPersonLocation,
  TelehealthPatientLocation,
  TelehealthPlatform,
  TelehealthProviderLocation,
  VisitModality,
} from '../types/visitLocation'

const visitModalityField: DropdownField<VisitModality> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Visit Modality',
  inputType: 'buttons',
  options: [
    { id: 'in-person', text: 'In-Person' },
    { id: 'telehealth-video', text: 'Telehealth — Video' },
    { id: 'telehealth-audio-only', text: 'Telehealth — Audio Only' },
  ],
}

const inPersonLocationField: DropdownField<InPersonLocation> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Visit Location',
  inputType: 'buttons',
  condition: v => v?.modality === 'in-person',
  options: [
    { id: 'office', text: 'Office' },
    { id: 'home', text: 'Home' },
    { id: 'birthing-center', text: 'Birthing Center' },
    { id: 'mobile-clinic', text: 'Mobile Clinic (Coming Soon)', disabled: true },
    { id: 'other', text: 'Other' },
  ],
}

const locationOtherField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Specify location',
  condition: v => v?.modality === 'in-person' && v?.location === 'other',
}

const telehealthPlatformField: DropdownField<TelehealthPlatform> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'HIPAA-Compliant Platform',
  condition: v => v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only',
  options: [
    { id: 'zoom', text: 'Zoom for Healthcare' },
    { id: 'doxy', text: 'Doxy.me' },
    { id: 'spruce', text: 'Spruce Health' },
    { id: 'other', text: 'Other (specify)' },
  ],
}

const platformOtherField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Specify HIPAA-compliant platform',
  condition: v =>
    (v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only') &&
    v?.platform === 'other',
}

const hipaaComplianceConfirmationField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder:
    'I confirm that the platform used meets HIPAA technical safeguard requirements for protected health information.',
  condition: v =>
    (v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only') &&
    v?.platform === 'other',
}

const patientLocationField: DropdownField<TelehealthPatientLocation> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Patient Location at Time of Visit',
  inputType: 'radio',
  condition: v => v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only',
  options: [
    { id: 'home', text: 'Patient Home' },
    { id: 'other', text: 'Other' },
  ],
}

const patientLocationOtherField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Specify patient location',
  condition: v =>
    (v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only') &&
    v?.patientLocation === 'other',
}

const providerLocationField: DropdownField<TelehealthProviderLocation> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Provider Location at Time of Visit',
  inputType: 'radio',
  condition: v => v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only',
  options: [
    { id: 'office', text: 'Office' },
    { id: 'birthing-center', text: 'Birthing Center' },
    { id: 'provider-home', text: 'Home' },
    { id: 'other', text: 'Other' },
  ],
}

const providerLocationOtherField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Specify provider location',
  condition: v =>
    (v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only') &&
    v?.providerLocation === 'other',
}

const patientConsentField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Patient verbally consented to receive care via telehealth today.',
  condition: v => v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only',
}

const synchronousField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'This visit was conducted as a live, interactive, real-time encounter.',
  condition: v => v?.modality === 'telehealth-video' || v?.modality === 'telehealth-audio-only',
}

const videoConfirmationField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder:
    'Patient and provider were visible to each other via video throughout this encounter.',
  condition: v => v?.modality === 'telehealth-video',
}

const videoCapableField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'I was technically capable of conducting this visit via video.',
  condition: v => v?.modality === 'telehealth-audio-only',
}

const audioOnlyReasonField: DropdownField<AudioOnlyReason> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Reason Video Not Used',
  condition: v => v?.modality === 'telehealth-audio-only',
  options: [
    { id: 'religious-cultural', text: 'Religious / cultural practice' },
    { id: 'no-device', text: 'Does not own a smartphone or device with camera' },
    { id: 'no-internet', text: 'No internet access or insufficient bandwidth' },
    { id: 'unable-to-use', text: 'Patient not able to use video technology' },
    { id: 'no-privacy', text: 'Patient in a location where video is not private' },
    { id: 'declined', text: 'Patient declined video (no other reason stated)' },
    { id: 'other', text: 'Other (specify)' },
  ],
}

const audioOnlyReasonOtherField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Specify reason video was not used',
  condition: v => v?.modality === 'telehealth-audio-only' && v?.audioOnlyReason === 'other',
  hints: [{ text: 'Free-text audio-only justifications carry higher audit risk.' }],
}

export const visitLocationField: FieldMap = {
  name: 'Visit Location',
  calculateOnConditional: true,
  children: {
    modality: visitModalityField,
    location: inPersonLocationField,
    locationOther: locationOtherField,
    platform: telehealthPlatformField,
    platformOther: platformOtherField,
    hipaaComplianceConfirmation: hipaaComplianceConfirmationField,
    patientLocation: patientLocationField,
    patientLocationOther: patientLocationOtherField,
    providerLocation: providerLocationField,
    providerLocationOther: providerLocationOtherField,
    patientConsent: patientConsentField,
    synchronous: synchronousField,
    videoConfirmation: videoConfirmationField,
    videoCapable: videoCapableField,
    audioOnlyReason: audioOnlyReasonField,
    audioOnlyReasonOther: audioOnlyReasonOtherField,
  },
}

export const audioOnlyReasonChartText: Record<AudioOnlyReason, string> = {
  'religious-cultural':
    'Patient declined video due to religious or cultural practice that prohibits use of video technology. Provider confirmed capability to conduct video visit. Audio-only modality used per patient preference.',
  'no-device':
    'Patient does not have access to a device with video capability. Provider confirmed capability to conduct video visit. Audio-only modality used due to patient technology limitation.',
  'no-internet':
    'Patient does not have internet access or has insufficient bandwidth to support video. Provider confirmed capability to conduct video visit. Audio-only modality used due to connectivity limitation.',
  'unable-to-use':
    'Patient is unable to operate video technology. Provider confirmed capability to conduct video visit. Audio-only modality used due to patient technology barrier.',
  'no-privacy':
    'Patient was in a setting where video could not be conducted privately. Provider confirmed capability to conduct video visit. Audio-only modality used to protect patient privacy.',
  declined:
    'Patient declined video technology for this encounter. Provider confirmed capability to conduct video visit and offered it to patient. Patient elected audio-only.',
  other: '',
}
